function bundleFormatMoney(cents, format) {
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;

    if (isNaN(number) || number == null) return 0;

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '${{amount}}';
  const match = formatString.match(placeholderRegex);
  let value = '';

  switch (match && match[1]) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return formatString.replace(placeholderRegex, value);
}

if (!customElements.get('bundle-products')) {
  customElements.define(
    'bundle-products',
    class BundleProducts extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.moneyFormat = JSON.parse(this.querySelector('[data-bundle-money-format]').textContent);
        this.mainVariant = JSON.parse(this.querySelector('[data-bundle-main-variant]').textContent);
        const priceWrapper = document.getElementById('price-' + this.sectionId);
        this.priceRegularEl = priceWrapper?.querySelector('.price-item--regular');
        this.priceSaleEl = priceWrapper?.querySelector('.price-item--sale');
        this.submitButton = this.querySelector('[data-bundle-submit]');
        this.submitPriceEl = this.querySelector('[data-bundle-submit-price]');
        this.errorEl = this.querySelector('[data-bundle-error]');

        this.items = new Map();
        this.querySelectorAll('[data-bundle-item]').forEach((itemEl) => {
          const variants = JSON.parse(itemEl.querySelector('[data-bundle-item-variants]').textContent);
          this.items.set(itemEl, {
            productId: itemEl.dataset.productId,
            variants,
            variant: this.resolveVariant(itemEl, variants),
          });
          this.updateSelectionText(itemEl);
        });
      }

      connectedCallback() {
        this.addEventListener('change', this.onOptionChange.bind(this));
        // Switching the US/UK/EU tab re-labels the already-checked size without firing a
        // native `change` event, so the subtitle needs its own refresh on tab clicks too.
        // This listener is on the bubble phase, so it runs after the legend's own click
        // handler (in product-info.js) has already toggled the visible label.
        this.addEventListener('click', (event) => {
          const legend = event.target.closest('legend[data-label]');
          const itemEl = legend?.closest('[data-bundle-item]');
          if (itemEl) this.updateSelectionText(itemEl);
        });
        this.submitButton.addEventListener('click', this.onSubmit.bind(this));
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;
          this.mainVariant = event.data.variant;
          this.recalculate();
        });
        this.recalculate();
      }

      disconnectedCallback() {
        if (this.unsubscribeVariantChange) this.unsubscribeVariantChange();
      }

      selectedOptionsFor(itemEl) {
        return Array.from(itemEl.querySelectorAll('.bundle-products__option')).map(
          (fieldset) => fieldset.querySelector('input:checked')?.value
        );
      }

      resolveVariant(itemEl, variants) {
        const selectedOptions = this.selectedOptionsFor(itemEl);
        if (selectedOptions.length === 0) return variants.find((variant) => variant.available) || variants[0];

        return variants.find((variant) => variant.options.every((value, index) => value === selectedOptions[index]));
      }

      // A checked radio's `value` is always the underlying (US-scale) option value used
      // for variant matching. The US/UK/EU tabs only relabel it visually via sibling
      // <label> elements toggling a `hidden` class, so the display text has to come from
      // whichever of those labels is currently visible, not from the raw input value.
      visibleLabelTextFor(input) {
        if (!input) return null;
        const candidates = [];
        let sibling = input.nextElementSibling;
        while (sibling && sibling.tagName === 'LABEL' && candidates.length < 3) {
          candidates.push(sibling);
          sibling = sibling.nextElementSibling;
        }
        if (!candidates.length) return input.value;
        const visible = candidates.find((label) => !label.classList.contains('hidden')) || candidates[0];
        // Every label carries a `.visually-hidden` a11y span (sold-out/unavailable copy)
        // regardless of stock status, so it must be stripped by removing the node rather
        // than by matching English text — textContent includes hidden nodes verbatim.
        const clone = visible.cloneNode(true);
        clone.querySelectorAll('.visually-hidden').forEach((el) => el.remove());
        return clone.textContent.trim();
      }

      updateSelectionText(itemEl) {
        const selectionEl = itemEl.querySelector('[data-bundle-item-selection]');
        if (!selectionEl) return;
        const checkedInputs = Array.from(itemEl.querySelectorAll('.bundle-products__option')).map((fieldset) =>
          fieldset.querySelector('input:checked')
        );
        selectionEl.textContent = checkedInputs
          .map((input) => this.visibleLabelTextFor(input))
          .filter(Boolean)
          .join(' / ');
      }

      onOptionChange(event) {
        const itemEl = event.target.closest('[data-bundle-item]');
        if (!itemEl) return;

        const itemState = this.items.get(itemEl);
        itemState.variant = this.resolveVariant(itemEl, itemState.variants);

        this.updateSelectionText(itemEl);

        this.recalculate();
      }

      recalculate() {
        let total = this.mainVariant ? this.mainVariant.price : 0;
        let allAvailable = !!this.mainVariant && this.mainVariant.available !== false;

        this.items.forEach((itemState) => {
          if (!itemState.variant || itemState.variant.available === false) {
            allAvailable = false;
            return;
          }
          total += itemState.variant.price;
        });

        const formattedTotal = bundleFormatMoney(total, this.moneyFormat);
        if (this.priceRegularEl) this.priceRegularEl.innerHTML = formattedTotal;
        if (this.priceSaleEl) this.priceSaleEl.innerHTML = formattedTotal;
        if (this.submitPriceEl) this.submitPriceEl.innerHTML = formattedTotal;
        this.submitButton.disabled = !allAvailable;

        // Yalla Points (main-kit-product.liquid) quotes points off this same bundle
        // total, not just the base variant — keep it in sync with every recalculation.
        const yallaPointsEl = document.getElementById('yallaPoints');
        if (yallaPointsEl) yallaPointsEl.textContent = Math.floor(total / 100);
      }

      getCart() {
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      showError(message) {
        this.errorEl.textContent = message || window.cartStrings.error;
        this.errorEl.removeAttribute('hidden');
      }

      hideError() {
        this.errorEl.setAttribute('hidden', '');
        this.errorEl.textContent = '';
      }

      onSubmit() {
        if (this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.hideError();
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        const spinner = this.submitButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const variantIds = [
          this.mainVariant.id,
          ...Array.from(this.items.values()).map((itemState) => itemState.variant.id),
        ];
        const bundleId = 'bp-' + variantIds.map(String).sort().join('-');

        const items = [{ id: this.mainVariant.id, quantity: 1, properties: { _bundle_id: bundleId } }];
        this.items.forEach((itemState) => {
          items.push({
            id: itemState.variant.id,
            quantity: 1,
            properties: { _bundle_id: bundleId, _bundle_child: 'true' },
          });
        });

        const cart = this.getCart();
        const sectionIds = cart ? cart.getSectionsToRender().map((section) => section.id) : [];
        if (cart) cart.setActiveElement(document.activeElement);

        const config = fetchConfig('json');
        config.body = JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        });

        fetch(window.routes.cart_add_url, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'bundle-products',
                productVariantId: items[0].id,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.showError(response.description || response.message);
              return;
            }

            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const lastItem = response.items[response.items.length - 1];
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bundle-products',
              productVariantId: lastItem.id,
              cartData: response,
            }).then(() => {
              cart.renderContents({ key: lastItem.key, sections: response.sections });
            });
          })
          .catch((error) => {
            console.error(error);
            this.showError();
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
            this.submitButton.removeAttribute('aria-disabled');
            if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          });
      }
    }
  );
}
